// packages
import React, { useCallback, useEffect, useState, useRef, useMemo, ReactNode, createContext, useContext } from "react";
import { useDeepCompareMemo } from "@react-hookz/web";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";

// components
import { CoverageIconGrid } from "@/shared/components/CoverageIconGrid";
import { Button } from "@/shared/components/ui/Button";
import { Heading } from "@/shared/components/ui/Heading";
import { MultiPetSelect } from "./MultiPetSelect";
import MultipetToggle from "./MultipetToggle";
import TakeoverProvision from "./TakeoverProvision";
import { CustomizationSlot } from "./CustomizationSlot";
import QuickEnrollPlans from "./QuickEnrollPlans";
import { MultiPlanGuide } from "./CoverageEditor/MultiPlanGuide";
import { useMultiPlanGuideNudge } from "./CoverageEditor/MultiPlanGuide/useMultiPlanGuideNudge";
import { buildRecommendedPlanGroups, RecommendedPlanGroup, ResolvedRecommendedPlan } from "./CoverageEditor/MultiPlanGuide/MultiPlanGuideUtils";

// utils
import { CoverageUtils, CoverageValue, PreventiveOption, TransactionFeeType } from "@/shared/utils/CoverageUtils";
import { PresetCoverageLevel, PreventiveConfig } from "@/shared/utils/CoverageUtils";
import { BuilderUtils } from "@/shared/utils/BuilderUtils";
import { AnalyticsUtils } from "../utils/AnalyticsUtils";
import { cn } from "../utils";
import ErrorUtils from "../utils/ErrorUtils";
import Strings from "../utils/Strings.constants";
import { QuoteDataUtils } from "../utils/QuoteDataUtils";

// hooks
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { useFormParentContext } from "@/shared/contexts/FormParent";
import { useStatsig } from "../hooks/useStatsig";
import { DefaultPlanConfig, useDefaultPlans } from "../hooks/useDefaultPlans";
import { useModal } from "@/shared/hooks/useModal";
import { useHasBreedConditions } from "../hooks/useHasBreedConditions";
import { useCheckoutCtaScroll } from "../hooks/useCheckoutCtaScroll";
import { useModal as useModalProvider } from "@/shared/contexts/ModalProvider";

// types
import { CoverageSettings, Option, Quote, Pet, InitialPetValues } from "@/shared/types/Quote.interface";
import { FormStepProps } from "@/shared/types/Form";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";
import { PetPolicy } from "@spotpetinsurance/spot-types/entities/PetPolicy";
import PetModal, { PetModalConfig } from "./PetModal";
import UserModal, { UserModalConfig } from "./UserModal";
import { UserEditTrigger } from "./UserEditTrigger";
import { PRIORITY_CODE_DEPRECATED } from "@/shared/types/SpotAPI";
import { UsQuoteFormSteps } from "@/quote-ptz-us/formConfigTypes";
import { WaitingPeriods } from "./FullCoverageDetailsModal/coverageData";

// config
import { PublicConfig } from "../PublicConfig";

// media
import { PreExistingConfig } from "@/quote-ptz-us/formController";
import PreExistingConditionsBanner from "./ui/PreExistingConditionsBanner";
import { CoverageLevel, CoverageTypeControl } from "./CoverageEditor/CoverageTypeControl";
import { CoverageParameterControls } from "./CoverageEditor/CoverageParameterControls";
import { PreventiveCare } from "./CoverageEditor/PreventiveCare";
import { CoverageDisplayProvider } from "../contexts/CoverageDisplayContext";
import { CoverageSectionHeader } from "./CoverageEditor/CoverageSectionHeader";
import BreedConditionFacts from "./BreedConditionFacts";
import ReimbursementTerms from "./ReimbursementTerms";
import { useFormMethodsContext } from "./Form";
import LoadingSpinner from "./LoadingSpinner";
import { BuilderContent } from "@builder.io/sdk-react";
import { QueryClient } from "@tanstack/react-query";
import AiInsights from "./AiInsights";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
import { getAiInsightsByBreed } from "./AiInsights/aiInsightsData";
import { UIUtils } from "../utils/UIUtils";
import { ADD_PET_MODAL_TRIGGERS, CHECKOUT_CTA_VARIANTS, isCheckoutCtaVariant } from "../utils/constants";
import OneIcon from "@/shared/media/icons/one.svg";
import FullCoverageDetailsButton from "./FullCoverageDetailsButton";
import FullCoverageDetailsContent from "./FullCoverageDetailsModal";
import { FloatingCheckoutCta } from "./CoverageEditor/CheckoutCta";

export type CoverageModalTerms = "reimbursement" | "annualLimit" | "annualDeductible";

export interface UpdateCoverageSettingsProps {
    newCoverageSettings: CoverageSettings | null;
    isApplyToAllPetsChecked: boolean;
    petId?: string;
}

export interface HandleCustomAmountsProps {
    change: { setting: "annualLimit" | "reimbursementRate" | "annualDeductible"; value: string };
    draftCoverageSettings?: { [petId: string]: CoverageSettings } | null;
    petId?: string;
}

type CustomCoverageProps = {
    editorConfig: {
        defaultPlanConfig: DefaultPlanConfig;
        formId: string;
        title?: string;
        coveragePresetData?: PresetCoverageLevel[];
        termsInModal?: CoverageModalTerms[];
        samplePolicyUrl?: string;
        preventiveConfig: PreventiveConfig;
        includeTransactionFee?: boolean;
        feeType?: TransactionFeeType;
        exclusions?: React.ReactElement;
        customizationSlot?: ReactNode;
        petModalConfig: PetModalConfig;
        userModalConfig?: UserModalConfig;
        getModalInitialValues: (defaultCoverageSettings?: CoverageSettings, isMultiPetLinked?: boolean) => InitialPetValues;
        showTakeoverProvision?: boolean;
        priorityCode?: string;
        multiPetVariant: "card" | "hybrid" | "dropdown";
        showTelehealth?: boolean;
        isDentalCovered?: boolean;
        isMicrochippingCovered?: boolean;
        preExistingConditions?: string;
        waitingPeriods?: WaitingPeriods;
        showCompellingStats?: boolean;
        showButtonAnimations?: boolean;
        floatingVariant?: "default" | "floating" | "floating-inner" | "icon";
        showBreedSpecificBadges?: boolean;
        showCoverageInformationFlagEnabled?: boolean;
        isPreExistingBannerEnabled?: boolean;
        showStepLabel?: boolean;
        isCategorizedPreventiveEnabled?: boolean;
        builderAboveCardData?: any | undefined;
        queryClient?: QueryClient;
        isAiInsightsEnabled?: boolean;
        isTermsDefinitionsEnabled?: boolean;
        allowClickablePreventiveCards?: boolean;
        // ENG-18988: opt this form into the `prevCareCompare` side-by-side table test.
        // Gates exposure too, so forms that aren't in the test never log one.
        allowPreventiveCompareTable?: boolean;
        allowMultiPlanGuide?: boolean;
        // ENG-17301: opt this form into the `showCheckoutBtn` coverage-step checkout CTA test.
        // Gates exposure too, so forms that aren't in the test never log one.
        allowCheckoutCtaTest?: boolean;
        showRebrandTheme?: boolean;
        showReimbursementTip?: boolean;
        showStickyPriceLabel?: boolean;
        showNumbered?: boolean;
        // When true, automatically apply the default plan (resolved from fetched pet plans) to any
        // pet that arrives on the coverage step without coverage, so the quote is priced without a
        // manual change. Enabled for ptz-ca (which isn't seeded at quote creation); leave off for
        // forms that already seed coverage (e.g. ptz-us) to avoid double-applying.
        autoApplyDefaultPlan?: boolean;
        // Skip optimistic pricing when any pet has a discount — show "Calculating…" until the server responds.
        disableOptimisticDiscountPricing?: boolean;
    };
};

type CoverageEditorProps = FormStepProps<Quote> & CustomCoverageProps;

export function CoverageEditor(props: CoverageEditorProps) {
    const {
        formId,
        title = Strings.CREATE_PLAN,
        defaultPlanConfig,
        coveragePresetData,
        termsInModal = ["annualDeductible", "annualLimit", "reimbursement"],
        samplePolicyUrl = "",
        preventiveConfig,
        includeTransactionFee = true,
        feeType = "per-quote",
        exclusions,
        customizationSlot,
        petModalConfig,
        userModalConfig,
        showTakeoverProvision = false,
        priorityCode,
        multiPetVariant,
        showTelehealth,
        isDentalCovered,
        showStickyPriceLabel,
        showNumbered,
        isMicrochippingCovered,
        getModalInitialValues,
        preExistingConditions,
        waitingPeriods,
        showCompellingStats,
        showButtonAnimations, // used in MultiPetSelectCard from context
        floatingVariant,
        showBreedSpecificBadges,
        isPreExistingBannerEnabled,
        showStepLabel,
        builderAboveCardData,
        queryClient,
        isCategorizedPreventiveEnabled,
        isAiInsightsEnabled,
        isTermsDefinitionsEnabled,
        allowClickablePreventiveCards = false,
        allowPreventiveCompareTable = false,
        allowMultiPlanGuide = false,
        allowCheckoutCtaTest = false,
        showRebrandTheme = false,
        showReimbursementTip = false,
        autoApplyDefaultPlan = false,
        disableOptimisticDiscountPricing = false
    } = props?.editorConfig || {};

    // local state
    const [customCoverageSettings, setCustomCoverageSettings] = useState<CoverageValue | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const coverageLevelIndex = CoverageUtils.DEFAULT_COVERAGE_INDEX;
    const initialRef = useRef(false);
    // Tracks pets we've already auto-applied a default plan to, so the effect runs once per pet.
    const defaultPlanAppliedRef = useRef<Set<string>>(new Set());
    const modal = useModal();
    const { showModal } = useModalProvider();

    // form state
    const { setValue, getValues, watch } = useFormParentContext<Quote>();

    const quote = watch();
    const policies = watch("policies");

    // hooks
    const { appState, updateAppState } = useAppLayerContext();
    const {
        isMultiPetLinked,
        updateQuote,
        isQuoteUpdating,
        isApplyAllHidden,
        underwriter,
        petPlansObject,
        mockAPIError,
        activePetIndex = 0,
        scrollToAddedPet,
        preExistingConfig,
        showBreedSpecificConditions,
        draftCoverageSettings,
        preservedCoverageByPet,
        addPetModal,
        showQuickEnrollUI,
        existingPolicies,
        quickEnrollHasCustomized
    } = appState;

    // aggregators test
    const formMethods = useFormMethodsContext();
    const onFormContinue = formMethods?.onFormContinue;
    const isValidating = !!formMethods?.isValidating;
    const canContinue = formMethods?.canContinue ?? true;
    const continueButtonRef = useRef<HTMLButtonElement>(null);

    const initialAboveCardContent = queryClient?.getQueryData(["builder-ssr-content", "above-card"]) as BuilderContent | undefined;
    const hasAboveCardDecor = builderAboveCardData?.useCustomHeaderBackground ?? initialAboveCardContent?.data?.useCustomHeaderBackground ?? false;
    // test end

    const { petPlans, isPetPlansUpdating, ratingZipcode: plansRatingZipcode } = petPlansObject || {};

    const builderUtils = new BuilderUtils(underwriter as PetUnderwriterType);

    /// pre-existing conditions test hook
    useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "preExistingConfig", defaultValue: {} as PreExistingConfig }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });
    /// hook ends

    // $3k annual limit test
    useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "defaultAnnualLimit", defaultValue: 3000 }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });

    // Statsig - Ai Insights flag
    const { showAiInsights } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showAiInsights", defaultValue: false }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });

    // Statsig - What's not covered - accident and illness FOMO
    const { showNotCovered } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showNotCovered", defaultValue: false }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });

    // Statsig - What's not covered - platinum recommendation
    const { showPlatinumRecommendation } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showPlatinumRecommendation", defaultValue: false }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });

    const shouldShowBreedSpecificBadges = showBreedSpecificBadges && !showAiInsights;

    // Statsig - ENG-19039 owner info CTA gravitas test. Restyles the step-2 "Edit my info" trigger
    // (filled pill at rest, teal on hover) and, at 2xs/xs only, opens 12px between it and the pet card.
    const { showOwnerCtaGravitas } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showOwnerCtaGravitas", defaultValue: false }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });
    // test end

    const userEditTrigger = userModalConfig && (
        <UserEditTrigger firstName={quote?.firstName} lastName={quote?.lastName} onClick={() => setIsUserModalOpen(true)} showOwnerCtaGravitas={showOwnerCtaGravitas} />
    );

    // Statsig - Actionable section title test
    const { showFullCoverageDetails } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showFullCoverageDetails", defaultValue: false }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });
    // test end

    // Statsig - ENG-19040 Full Coverage Details CTA gravitas test. Restyles the CTA itself (teal,
    // underlined, small label, 4px padding) at every breakpoint; the button's placement is unchanged.
    const { showDetailsCtaGravitas } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showDetailsCtaGravitas", defaultValue: false }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams)
    });
    // test end
    // clickable cards tests — gated per form via editorConfig.allowClickablePreventiveCards (e.g. US only, not CA);
    const { showClickableCards: showClickableCardsTest } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showClickableCards", defaultValue: false }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams),
        deferExposureUntil: allowClickablePreventiveCards
    });
    const showClickableCards = allowClickablePreventiveCards && showClickableCardsTest;

    // test start ENG-18988 prevCareCompare — gold/platinum side-by-side comparison table.
    // Read once here (not in PreventiveCare) so the section shares a single exposure, and deferred
    // until the form actually allows the table — US coverage step only, per editorConfig.
    const { prevCareCompare } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "prevCareCompare", defaultValue: "" }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams),
        deferExposureUntil: allowPreventiveCompareTable
    });
    const prevCareCompareVariant = allowPreventiveCompareTable ? prevCareCompare : undefined;
    // test end

    // Statsig - ENG-17301 coverage-step "Proceed to Checkout" CTA. `""` (control) and any
    // unrecognised value render nothing; gated per form via editorConfig.allowCheckoutCtaTest.
    const { showCheckoutBtn } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "showCheckoutBtn", defaultValue: "" }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams),
        deferExposureUntil: allowCheckoutCtaTest
    });
    const checkoutCtaVariant = allowCheckoutCtaTest && isCheckoutCtaVariant(showCheckoutBtn) ? showCheckoutBtn : undefined;

    const { coverageEndRef, preventiveEndRef, isCheckoutCtaVisible } = useCheckoutCtaScroll({
        enabled: !!checkoutCtaVariant,
        footerCtaRef: appState.formContinueButtonRef
    });

    // The sticky menu lives outside this tree (portalled from MultiPetSelectCard), so the resolved
    // variant and the scroll window are published through appState rather than prop-drilled.
    // Skipped entirely for the control group: `updateAppState` deep-compares and re-renders every
    // consumer in the form, so no user outside the test should pay for this.
    useEffect(() => {
        if (!checkoutCtaVariant) return;
        updateAppState({ checkoutCtaVariant, isCheckoutCtaVisible });
    }, [checkoutCtaVariant, isCheckoutCtaVisible, updateAppState]);

    // Leaving the step must not leave a checkout pill behind in a later step's sticky menu, where it
    // would submit that step instead.
    useEffect(() => {
        return () => updateAppState({ checkoutCtaVariant: undefined, isCheckoutCtaVisible: false });
    }, [updateAppState]);

    // Initial data
    const allPets = useDeepCompareMemo(() => policies ?? [], [policies]);
    const activePet = useDeepCompareMemo(() => policies?.[activePetIndex], [policies, activePetIndex]);
    const isAnnualBilling = appState.isAnnualBilling;
    const activePetAllPlans = useMemo(() => petPlans?.[activePet?.id ?? ``] ?? [], [activePet?.id, petPlans]);
    const policyPets: Option[] = useMemo(() => allPets.map(pet => ({ value: pet.id ?? ``, label: pet.name ?? `` })), [allPets]);
    const activePetBreed = allPets[appState.activePetIndex || 0]?.breedName;

    // Not manually memoized: `quote` comes from `watch()`, which returns a fresh object every render,
    // so a memo keyed on it recomputed every render anyway (and its `quote` dep is read again below,
    // which the React Compiler treats as a possible later mutation, blocking compilation).
    const priceInfoData = CoverageUtils.getPriceInfoData({ value: quote, includeTransactionFee, feeType, petIdToShow: !isMultiPetLinked ? activePet?.id : undefined });

    // format pet plan API data for UI components
    const allCoverageOptions = useMemo(
        () => (!!activePetAllPlans.length ? CoverageUtils.formatCustomCoverageData(activePetAllPlans, isAnnualBilling) : null),
        [activePetAllPlans, isAnnualBilling]
    );

    const { coverageTypes, preventiveCareOptions } = allCoverageOptions || {};

    // find options that have multiple values for matching multipet coverage settings
    const coverageValuesToMatch = useMemo(() => {
        if (!!allCoverageOptions) {
            const valuesWithMultipleOptions = CoverageUtils.findKeysWithMultipleOptions(allCoverageOptions);
            const matchCriteriaKeys = CoverageUtils.convertKeysToMatchCriteria(valuesWithMultipleOptions);
            return matchCriteriaKeys;
        }
        return [];
    }, [allCoverageOptions]);

    // Edit counter runs in both arms: the 15-edit threshold also gates the exposure below.
    const { registerEdit, hasReachedEditThreshold, isNudgeVisible, dismissNudge } = useMultiPlanGuideNudge();

    // Only the fields plan resolution/pricing reads. `allPets` changes on every coverage
    // edit (it carries coverageSettings + prices), so depending on it directly would
    // re-resolve every plan on every keystroke — for control users too.
    const multiPlanPetIdentities = useDeepCompareMemo(() => allPets.map(pet => ({ id: pet.id, name: pet.name, species: pet.species, discounts: pet.discounts })), [allPets]);

    // The pre-built plans per workflow group; null when any in-scope pet can't
    // resolve all 3 plans (feature degrades to "never offered", incl. no exposure).
    // Deferred until the threshold: the guide is unreachable before it, so neither arm needs this work during the first 14 edits.
    const resolvedPlanGroups = useMemo(
        () =>
            allowMultiPlanGuide && hasReachedEditThreshold
                ? buildRecommendedPlanGroups({
                      pets: multiPlanPetIdentities,
                      petPlans,
                      coverageValuesToMatch,
                      isMultiPetLinked: !!isMultiPetLinked
                  })
                : null,
        [allowMultiPlanGuide, hasReachedEditThreshold, multiPlanPetIdentities, petPlans, coverageValuesToMatch, isMultiPetLinked]
    );

    // Hold the last resolved groups so a transient null (pet plans refetching after
    // "Add a pet") can't unmount the guide mid-interaction and drop a visible toast.
    // Invalidated when the pet set changes, so stale groups can never be applied.
    // Held in state (set during render, the "store information from previous renders" pattern) rather
    // than a ref so it can be read during render; each branch only sets when the value would change.
    const [lastResolvedPlanGroups, setLastResolvedPlanGroups] = useState<{ petCount: number; groups: RecommendedPlanGroup[] } | null>(null);
    if (resolvedPlanGroups?.length) {
        if (lastResolvedPlanGroups?.groups !== resolvedPlanGroups || lastResolvedPlanGroups?.petCount !== allPets.length) {
            setLastResolvedPlanGroups({ petCount: allPets.length, groups: resolvedPlanGroups });
        }
    } else if (lastResolvedPlanGroups && lastResolvedPlanGroups.petCount !== allPets.length) {
        setLastResolvedPlanGroups(null);
    }
    const recommendedPlanGroups = resolvedPlanGroups ?? lastResolvedPlanGroups?.groups ?? null;
    const isMultiPlanGuideAvailable = !!recommendedPlanGroups?.length;

    // Exposure fires when the toast becomes eligible (Nth edit), not on mount.
    // `!multiPlanGuideDone` keeps a dismissed user from re-exposing on a later remount, when the nudge can no longer be shown to them.
    const { coverageEnableMultiPlanGuide } = useStatsig({
        layerId: PublicConfig.STATSIG_LAYER_ID_MAIN,
        layerParameters: [{ key: "coverageEnableMultiPlanGuide", defaultValue: false }] as const,
        queryParams: AnalyticsUtils.getQueryParams(quote?.extra?.queryParams),
        deferExposureUntil: allowMultiPlanGuide && isMultiPlanGuideAvailable && hasReachedEditThreshold && !appState.multiPlanGuideDone
    });
    const isMultiPlanGuideEnabled = allowMultiPlanGuide && coverageEnableMultiPlanGuide && isMultiPlanGuideAvailable;

    const transactionFee = includeTransactionFee ? (getValues("transactionFee.value") ?? 0) : 0;

    const coveragePresets = useMemo(() => {
        if (!!activePet?.id && !!petPlans && !!coveragePresetData) {
            const activePetPlans = petPlans[activePet.id];
            if (!!activePetPlans) {
                return CoverageUtils.getPresetPlansFromApiData({ petPlans: activePetPlans, presetCoverageLevels: coveragePresetData });
            }
        }
        return [];
    }, [activePet?.id, petPlans, coveragePresetData]);

    // Per-pet plan resolved from defaultPlanConfig (level/type/details match) — used by auto-apply
    // so the default selection is explicit rather than dependent on coveragePresetData ordering.
    const defaultPlans = useDefaultPlans({ petPlans, defaultPlanConfig });

    const allPetsHaveSameCoverages = useMemo(() => {
        if (!!allPets.length) {
            const allCoverageSettings = allPets.map(pet => pet?.coverageSettings);
            if (!!allCoverageSettings.length) {
                return CoverageUtils.allPetCoverageSettingsMatch(allCoverageSettings, coverageValuesToMatch);
            }
        }
        return false;
    }, [allPets, coverageValuesToMatch]);

    const allPetsHaveFetchedPlans = useMemo(() => {
        if (!!petPlans && !isPetPlansUpdating) {
            const allPlanIds = Object.keys(petPlans);
            return allPets.every(pet => !!pet.id && allPlanIds.includes(pet.id));
        }
        return false;
    }, [allPets, isPetPlansUpdating, petPlans]);

    const allPetPlansHaveSameOptions = useMemo(() => CoverageUtils.allPetPlansHaveSameOptions(petPlans), [petPlans]);

    const activePetCoverage = useDeepCompareMemo(() => {
        const coverage = !!activePet && customCoverageSettings ? customCoverageSettings : coveragePresets[coverageLevelIndex];
        return coverage;
    }, [customCoverageSettings, coveragePresets, coverageLevelIndex, activePet, draftCoverageSettings]);

    const getWellnessLevel = (coverageSettings?: CoverageSettings) => {
        if (!coverageSettings) return "None";
        const wellnessCoverage = coverageSettings?.coverages?.find(c => c.type?.includes(`wellness`))?.type;
        return wellnessCoverage === "wellness-gold" ? "wellness-gold" : wellnessCoverage === "wellness-platinum" ? "wellness-platinum" : "None";
    };

    const [coverageLevel, setCoverageLevel] = useState<CoverageLevel>(
        activePet?.coverageSettings?.coverages?.some(coverage => coverage.type === "illness") ? "accidentAndIllness" : "accidentOnly"
    );

    const breedConditionsResult = useHasBreedConditions({
        isMultiPetLinked,
        policies,
        activePetBreed,
        species: activePet?.species,
        hasAccidentAndIllness: coverageLevel === "accidentAndIllness",
        isDentalCovered: isDentalCovered,
        enabled: showBreedSpecificConditions
    });

    const hasAnyBreedConditions = breedConditionsResult.hasConditions;
    const breedConditionData = breedConditionsResult.breedData;

    const petsForInsights = useMemo(() => {
        if (isMultiPetLinked) {
            return (
                policies
                    ?.map(policy => ({
                        name: policy.name ?? "",
                        breedName: policy.breedName ?? ""
                    }))
                    .filter(pet => pet.name && pet.breedName) ?? []
            );
        }

        // Single pet
        if (activePet?.name && activePet?.breedName) {
            return [
                {
                    name: activePet.name,
                    breedName: activePet.breedName
                }
            ];
        }

        return [];
    }, [isMultiPetLinked, policies, activePet]);

    const hasAnyPetWithAiInsights = useMemo(() => {
        return petsForInsights.some(pet => getAiInsightsByBreed(pet.breedName) !== undefined);
    }, [petsForInsights]);

    const [annualLimitValue, setAnnualLimitValue] = useState(activePet?.coverageSettings?.amounts?.annualLimit?.toString());
    const [annualDeductibleValue, setAnnualDeductibleValue] = useState(activePet?.coverageSettings?.amounts?.annualDeductible?.toString());
    const [reimbursementValue, setReimbursementValue] = useState(activePet?.coverageSettings?.amounts?.reimbursementRate?.toString());
    const [wellnessValue, setWellnessValue] = useState<"wellness-gold" | "wellness-platinum" | "None">(getWellnessLevel(activePet?.coverageSettings));
    const [lastPetEl, setLastPetEl] = useState<HTMLElement | null>(null);

    // Scroll to top when adding a pet in default coverage editor
    useEffect(() => {
        const scrollToTop = async () => {
            await UIUtils.scrollToTop(3000, 50);
        };

        if (scrollToAddedPet) {
            scrollToTop();
            updateAppState({ scrollToAddedPet: false });
        }
    }, [scrollToAddedPet, updateAppState]);

    const updatePricing = useCallback(
        (newCoverageSettings: CoverageSettings, targetPetId?: string, overrideIsMultiPetLinked?: boolean) => {
            const petIdToUse = targetPetId || activePet?.id;

            // Suppress optimistic pricing when any pet has a discount (see editorConfig doc).
            // Clear, don't just skip — a rapid second change would otherwise show the previous
            // change's stale optimistic price.
            if (disableOptimisticDiscountPricing) {
                const hasDiscounts = allPets.some(pet => !!pet.discounts?.length || (pet.discountAmount?.value ?? 0) > 0);
                if (hasDiscounts) {
                    updateAppState({ calculatedPriceData: undefined });
                    return;
                }
            }

            if (!!petPlans && !!petIdToUse) {
                const calculatedData = CoverageUtils.calculatePriceData({
                    coverageSettings: newCoverageSettings,
                    policies: allPets,
                    isMultiPetLinked: overrideIsMultiPetLinked ?? isMultiPetLinked, // Use override if provided
                    activePetId: petIdToUse,
                    petPlans: petPlans,
                    coverageValuesToMatch,
                    isAnnualBilling,
                    transactionFee,
                    feeType
                });

                const newCalculatedPriceData = {
                    preDiscountPrice: calculatedData.preDiscountPrice,
                    discountAmount: calculatedData.discountAmount,
                    finalPrice: calculatedData.finalPrice,
                    singlePetPriceData: calculatedData.perPetPriceData
                };

                updateAppState({
                    calculatedPriceData: newCalculatedPriceData
                });
            }
        },
        [activePet?.id, allPets, coverageValuesToMatch, disableOptimisticDiscountPricing, feeType, isAnnualBilling, isMultiPetLinked, petPlans, transactionFee, updateAppState]
    );

    // When transitioning from QuickEnroll → Customize, apply the current pet's coverage settings to CoverageEditor's local state
    // AND recompute pricing so the Customize UI doesn't show a stale appState.calculatedPriceData
    // prevShowQuickEnrollRef gate ensures the body only runs once per actual transition, even if updatePricing's identity changes.
    // Mirror a coverage config into the pickers' local state (single source for every
    // caller that applies settings wholesale rather than through one picker's handler).
    const syncPickerStateFromCoverage = useCallback((coverageSettings: CoverageSettings) => {
        const { amounts } = coverageSettings;
        setAnnualLimitValue(amounts?.annualLimit?.toString());
        setAnnualDeductibleValue(amounts?.annualDeductible?.toString());
        setReimbursementValue(amounts?.reimbursementRate?.toString());
        setWellnessValue(getWellnessLevel(coverageSettings));
        setCoverageLevel(coverageSettings.coverages?.some(c => c.type === "illness") ? "accidentAndIllness" : "accidentOnly");
    }, []);

    const prevShowQuickEnrollRef = useRef(showQuickEnrollUI);
    useEffect(() => {
        if (prevShowQuickEnrollRef.current && !showQuickEnrollUI && activePet?.coverageSettings) {
            syncPickerStateFromCoverage(activePet.coverageSettings);
            setCustomCoverageSettings({ name: "custom", config: activePet.coverageSettings });
            updatePricing(activePet.coverageSettings);
        }
        prevShowQuickEnrollRef.current = showQuickEnrollUI;
    }, [showQuickEnrollUI, activePet?.coverageSettings, updatePricing, syncPickerStateFromCoverage]);

    // `rethrowOnError` lets a caller observe a failed quote update instead of having it
    // swallowed here. Defaults to false so every existing call site keeps its behavior.
    const updateQuoteCoverage = useCallback(
        async (rethrowOnError = false) => {
            updateAppState({ isQuoteUpdating: true });
            const allValues = getValues();

            try {
                await updateQuote?.mutateAsync({ quote: allValues });
            } catch (error) {
                const hasPcodeError = ErrorUtils.hasSpotError(error, PRIORITY_CODE_DEPRECATED);

                if (hasPcodeError) {
                    let retryData = { ...allValues, affiliateCode: "SPOT", discountCode: "SPOT" };

                    if (PublicConfig.ENVIRONMENT === "development" && mockAPIError) {
                        const updatedExtra = { ...retryData.extra, sim: "" };
                        retryData = { ...retryData, extra: updatedExtra };
                    }

                    await updateQuote?.mutateAsync({ quote: retryData });
                } else if (rethrowOnError) {
                    throw error;
                }
            }
        },
        [getValues, mockAPIError, updateQuote, updateAppState]
    );

    const updateCoverageSettings = useCallback(
        async (newCoverageSettings: CoverageSettings | null, isApplyToAllPetsChecked: boolean, petId?: string, rethrowOnError = false) => {
            if (!newCoverageSettings) return;
            // Track that the user has customized coverage in the quick enroll flow
            if (showQuickEnrollUI === false && !quickEnrollHasCustomized) {
                updateAppState({ quickEnrollHasCustomized: true });
            }
            // Only update the customCoverageSettings state if we're modifying the active pet
            if (!petId || activePet?.id === petId) {
                setCustomCoverageSettings({ name: `custom`, config: newCoverageSettings });
            }

            updatePricing(newCoverageSettings, petId, isApplyToAllPetsChecked);

            const { policies } = getValues();
            if (!!policies?.length) {
                if (isApplyToAllPetsChecked && !petId) {
                    // Apply to all pets - but only when no specific petId is provided
                    policies.forEach((policy, index) => {
                        setValue(`policies.${index}.coverageSettings`, newCoverageSettings);
                    });
                } else if (petId) {
                    // Update the specific pet by ID
                    const petIndex = policies.findIndex(p => p.id === petId);
                    if (petIndex !== -1) {
                        setValue(`policies.${petIndex}.coverageSettings`, newCoverageSettings);
                    } else {
                        console.error("Pet not found:", petId);
                        return; // Exit early if pet not found
                    }
                } else {
                    // Update only the active pet (default behavior)
                    setValue(`policies.${activePetIndex}.coverageSettings`, newCoverageSettings);
                }
            }

            await updateQuoteCoverage(rethrowOnError);
        },
        [
            activePet?.id,
            activePetIndex,
            setCustomCoverageSettings,
            setValue,
            updatePricing,
            updateQuoteCoverage,
            getValues,
            showQuickEnrollUI,
            quickEnrollHasCustomized,
            updateAppState
        ]
    );

    // Apply a pre-built plan in one shot. The resolved config already matches a real
    // plan, so this bypasses handleCustomAmounts' match-or-no-op path.
    // Rethrows on a failed quote update so the guide can roll back its optimistic selection instead of confirming an apply the server never accepted.
    const applyRecommendedPlan = useCallback(
        async (group: RecommendedPlanGroup, plan: ResolvedRecommendedPlan) => {
            const applyToAll = group.groupKey === "all-pets";

            // updateCoverageSettings writes the form, local coverage state and optimistic
            // pricing *before* the PUT resolves, so snapshot them to undo on rejection —
            // otherwise a failed apply leaves the rejected plan in the form (and in the
            // next quote update) while the modal shows it as unselected.
            const previousCoverageByPetId = new Map((getValues().policies ?? []).map(policy => [policy.id, policy.coverageSettings]));
            const previousCustomCoverage = customCoverageSettings;

            // A per-pet group gives each pet its own plan, so the quote must stop being linked —
            // otherwise the next ordinary coverage edit fans out and overwrites these independent
            // selections. Cleared *before* the apply so the PUT carries `extra.isMultiPetLinked:
            // false`; setting it afterwards would leave the server linked until some later update,
            // and a reload in between would restore the linked state.
            const previousIsMultiPetLinked = getValues().extra?.isMultiPetLinked;
            const shouldUnlinkPets = !applyToAll && !!isMultiPetLinked;
            if (shouldUnlinkPets) {
                setValue("extra.isMultiPetLinked", false);
                updateAppState({ isMultiPetLinked: false });
            }

            try {
                if (applyToAll) {
                    await updateCoverageSettings(plan.config, true, undefined, true);
                } else {
                    await updateCoverageSettings(plan.config, false, group.petIds[0], true);
                }
            } catch (error) {
                const { policies } = getValues();
                policies?.forEach((policy, index) => {
                    const previousCoverage = previousCoverageByPetId.get(policy.id);
                    if (previousCoverage) {
                        setValue(`policies.${index}.coverageSettings`, previousCoverage);
                    }
                });
                setCustomCoverageSettings(previousCustomCoverage);
                const restoredActiveCoverage = previousCoverageByPetId.get(activePet?.id) ?? previousCustomCoverage?.config;
                if (restoredActiveCoverage) {
                    updatePricing(restoredActiveCoverage);
                }
                if (shouldUnlinkPets) {
                    setValue("extra.isMultiPetLinked", previousIsMultiPetLinked ?? true);
                    updateAppState({ isMultiPetLinked: true });
                }
                throw error;
            }

            // Mirror the pickers' local state when the active pet is in scope (same sync as the QuickEnroll → Customize transition)
            if (applyToAll || group.petIds.includes(activePet?.id ?? "")) {
                syncPickerStateFromCoverage(plan.config);
            }
        },
        [updateCoverageSettings, activePet?.id, syncPickerStateFromCoverage, getValues, setValue, customCoverageSettings, updatePricing, isMultiPetLinked, updateAppState]
    );

    const handleCustomAmounts = (props: HandleCustomAmountsProps) => {
        const { change, draftCoverageSettings, petId } = props;
        const baseCoverage = activePetCoverage;

        if (!baseCoverage || !activePetAllPlans) {
            console.error("Active pet or plans data is undefined.");
            return;
        }

        const updatedAmounts = {
            ...baseCoverage.config?.amounts,
            [change.setting]: Number(change.value)
        };

        const updatedCoverageSettings: CoverageSettings = {
            ...baseCoverage.config,
            amounts: updatedAmounts
        };

        const matchingPlan = CoverageUtils.getPlanDataFromCoverageSettings(activePetAllPlans, updatedCoverageSettings, coverageValuesToMatch);

        if (matchingPlan) {
            const newCoverageSettings = CoverageUtils.getCoverageSettingsFromPlanData(
                matchingPlan,
                wellnessValue === "None" ? undefined : (wellnessValue as "wellness-gold" | "wellness-platinum")
            );

            updateCoverageSettings(newCoverageSettings, !!isMultiPetLinked);
        } else {
            console.error("No matching plan found for the new coverage settings.");
        }
    };

    const handleCoverageLevelChange = (planLevel: CoverageLevel, draftCoverageSettings?: { [petId: string]: CoverageSettings }, petId?: string) => {
        // D1: product-type changes count toward the 15-edit nudge threshold
        registerEdit();
        const effectiveCoverage = activePet?.coverageSettings;
        let newCoverages = [...(effectiveCoverage?.coverages || [])];

        // Adjust coverage based on the coverageLevel change
        const hasIllnessCoverage = newCoverages.some(c => c.type === "illness");
        if (planLevel === "accidentAndIllness" && !hasIllnessCoverage) {
            // Push "illness" coverage to newCoverages with a temporary filler ID
            newCoverages.push({ type: "illness", id: "tempId", name: "Illness Coverage" });
        } else if (planLevel === "accidentOnly" && hasIllnessCoverage) {
            // Remove "illness" coverage if switching back to accidentOnly
            newCoverages = newCoverages.filter(c => c.type !== "illness");
        }

        const updatedCoverageSettings: CoverageSettings = {
            ...effectiveCoverage,
            coverages: newCoverages
        };

        const matchingPlan = CoverageUtils.getPlanDataFromCoverageSettings(activePetAllPlans, updatedCoverageSettings, coverageValuesToMatch);
        if (matchingPlan) {
            const newCoverageSettings = CoverageUtils.getCoverageSettingsFromPlanData(
                matchingPlan,
                wellnessValue === "None" ? undefined : (wellnessValue as "wellness-gold" | "wellness-platinum")
            );

            updateCoverageSettings(newCoverageSettings, !!isMultiPetLinked);
        } else {
            console.error("No matching plan found for the new coverage settings.");
        }
    };

    const handlePreventiveChange = (wellnessType: string, petId?: string) => {
        registerEdit(); // D1: preventive/wellness changes are coverage-option edits too
        // Get the target pet and its coverage settings
        const targetPet = petId ? allPets.find(p => p.id === petId) : activePet;

        if (!targetPet) {
            console.error("Target pet not found");
            return;
        }

        // Get target pet's coverage settings (or fall back to active pet coverage if none)
        const targetCoverageSettings = targetPet.coverageSettings || activePetCoverage?.config;
        if (!targetCoverageSettings) {
            console.error("No coverage settings available for pet");
            return;
        }

        // When wellnessType is "None", filter out all wellness coverages
        const isRemovingWellness = wellnessType === "None";

        // Clone existing coverages, excluding any existing wellness coverage
        const newCoverages = (targetCoverageSettings.coverages || []).filter(coverage => !coverage.type?.includes("wellness"));

        // Get the plans for the target pet
        const targetPetPlans = petPlans?.[targetPet.id!] ?? [];

        if (!isRemovingWellness && (wellnessType === "wellness-gold" || wellnessType === "wellness-platinum")) {
            const newCoverageSettings: CoverageSettings = {
                ...targetCoverageSettings,
                coverages: [{ type: wellnessType }]
            };

            const matchingPlan = CoverageUtils.getPlanDataFromCoverageSettings(targetPetPlans, newCoverageSettings, coverageValuesToMatch);

            if (matchingPlan) {
                const wellnessRider = CoverageUtils.getRiderFromCoverageType(wellnessType, matchingPlan.riders);
                if (wellnessRider && wellnessRider.id) {
                    // Add the matched wellness coverage with dynamically found id
                    newCoverages.push({
                        type: wellnessType,
                        id: wellnessRider.id,
                        preventivePrice: wellnessRider.basePrice?.value
                    });
                } else {
                    console.error("No matching wellness rider found for selected plan.");
                }
            } else {
                console.error("No matching plan found for updated wellness coverage settings.");
            }
        }

        // Create the updated coverage settings
        const updatedCoverageSettings = {
            ...targetCoverageSettings,
            coverages: newCoverages
        };

        // For a specific pet, we always want isApplyToAllPetsChecked=false
        // Pass the petId to updateCoverageSettings so it knows which pet to update
        updateCoverageSettings(updatedCoverageSettings, petId ? false : !!isMultiPetLinked, petId);
    };

    const handleActivePetChange = (newPetId: string) => {
        const currentPolicies = getValues().policies ?? [];
        const newActivePetIndex = currentPolicies.findIndex(pet => pet.id === newPetId);

        if (newActivePetIndex === -1) {
            console.warn("Pet not found in current policies");
            return;
        }

        updateAppState({ activePetIndex: newActivePetIndex });
        const selectedPet = currentPolicies[newActivePetIndex];
        if (selectedPet && selectedPet.coverageSettings) {
            const { coverageSettings } = selectedPet;
            if (coverageSettings.amounts?.annualLimit) {
                setAnnualLimitValue(coverageSettings.amounts.annualLimit.toString());
            }
            if (coverageSettings.amounts?.annualDeductible) {
                setAnnualDeductibleValue(coverageSettings.amounts.annualDeductible.toString());
            }
            if (coverageSettings.amounts?.reimbursementRate) {
                setReimbursementValue(coverageSettings.amounts.reimbursementRate.toString());
            }
            if (coverageSettings.coverages) {
                setCoverageLevel(coverageSettings.coverages.some(coverage => coverage.type === "illness") ? "accidentAndIllness" : "accidentOnly");
                setWellnessValue(getWellnessLevel(coverageSettings));
            }
            setCustomCoverageSettings({ name: "custom", config: coverageSettings });
        }
    };

    useEffect(() => {
        // Initialization
        if (!isQuoteUpdating && !isPetPlansUpdating && allPetsHaveFetchedPlans && !initialRef.current && !!activePet?.coverageSettings) {
            // If all pets do not have the same coverage options available, hide the "Apply to all pets" feature
            updateAppState({ isApplyAllHidden: !allPetPlansHaveSameOptions });
            initialRef.current = true;
            setIsInitialized(true);
        }
    }, [activePet?.coverageSettings, allPetPlansHaveSameOptions, allPetsHaveFetchedPlans, allPetsHaveSameCoverages, isPetPlansUpdating, isQuoteUpdating, petPlans, updateAppState]);

    // Seed the "custom" preset from the pet's saved coverage. The pet's settings usually arrive after
    // the first render (quote + plans resolve asynchronously), so this seeds during the render that
    // first has them rather than a commit later. It runs once: once seeded, the guard is false, and
    // every later write goes through updateCoverageSettings / the pet switcher.
    if (!customCoverageSettings && !!activePet?.coverageSettings) {
        setCustomCoverageSettings({ name: "custom", config: activePet.coverageSettings });
    }

    // When `autoApplyDefaultPlan` is enabled (ptz-ca only), keep the active pet on a plan that's VALID
    // for the current province's plans (CA plan ids are province-specific):
    //   - no coverage yet → apply the configured default, so the quote is priced without a manual change;
    //   - coverage whose id is stale (the customer changed province) → re-map to the equivalent plan in
    //     the new province's plans, matched by amounts, preserving the customer's selected level; if no
    //     equivalent exists, fall back to the default;
    //   - a selection already valid for this province → leave it untouched.
    // We resolve real plan ids (not the symbolic default-plan id) because the backend only prices a real
    // plan. Runs once per pet (per mount). Gated behind the per-form flag so it never fires on ptz-us.
    useEffect(() => {
        if (!autoApplyDefaultPlan) return;
        const petId = activePet?.id;
        if (!petId) return;
        // Only act on plans that belong to the quote's CURRENT province. After a postal change there's a
        // render window where usePetPlans still holds the previous province's plans (plan ids are
        // province-specific), and applying one would PUT an invalid id (→ 500 → QuoteResetModal). Wait
        // until the fetched plans' postal matches the quote's current postal.
        const plansMatchProvince = UIUtils.normalizePostalCode(plansRatingZipcode) === UIUtils.normalizePostalCode(quote?.ratingZipcode);
        if (!allPetsHaveFetchedPlans || isPetPlansUpdating || isQuoteUpdating || !plansMatchProvince || defaultPlanAppliedRef.current.has(petId)) return;

        const coverageSettings = activePet?.coverageSettings;
        const hasCoverage = !!coverageSettings?.coverages?.length;

        // A selection is valid only if its base coverage id is one of the current province's plan ids.
        const planIds = new Set(activePetAllPlans.map(plan => plan.baseCoverage.id).filter(Boolean));
        const selectedId = coverageSettings?.coverages?.find(coverage => coverage.type === "accident" || coverage.type === "illness")?.id;
        if (hasCoverage && !!selectedId && planIds.has(selectedId)) return;

        // Resolution order: (1) a selection preserved from before a province change → the equivalent
        // plan in this province (keeps the customer's level); (2) a stale current selection → re-map it;
        // (3) otherwise → the configured default. Real plan ids only (the backend prices real plans).
        let resolvedPlan: CoverageSettings | null | undefined;

        const preservedForPet = preservedCoverageByPet?.[petId];
        if (preservedForPet) {
            const matchingPlan = CoverageUtils.getPlanDataFromCoverageSettings(activePetAllPlans, preservedForPet, coverageValuesToMatch);
            const wellness = getWellnessLevel(preservedForPet);
            resolvedPlan = matchingPlan ? CoverageUtils.getCoverageSettingsFromPlanData(matchingPlan, wellness === "None" ? undefined : wellness) : undefined;
            // Consume the stash regardless of whether it matched (a no-match falls through to the default
            // below); clearing prevents it from re-applying on a later legitimate edit.
            const rest = { ...preservedCoverageByPet };
            delete rest[petId];
            updateAppState({ preservedCoverageByPet: Object.keys(rest).length ? rest : null });
        }

        if (!resolvedPlan && hasCoverage && coverageSettings) {
            const matchingPlan = CoverageUtils.getPlanDataFromCoverageSettings(activePetAllPlans, coverageSettings, coverageValuesToMatch);
            const wellness = getWellnessLevel(coverageSettings);
            resolvedPlan = matchingPlan ? CoverageUtils.getCoverageSettingsFromPlanData(matchingPlan, wellness === "None" ? undefined : wellness) : undefined;
        }
        if (!resolvedPlan) {
            // Resolve from defaultPlanConfig (via useDefaultPlans), not a positional preset index, so the
            // applied plan can't silently change if coveragePresetData is ever reordered.
            const matchedDefaultPlan = defaultPlans[petId];
            resolvedPlan = matchedDefaultPlan ? QuoteDataUtils.extractCoveragesFromPetPolicy({ coverages: [matchedDefaultPlan.baseCoverage] } as unknown as PetPolicy) : undefined;
        }
        if (!resolvedPlan) return;

        defaultPlanAppliedRef.current.add(petId);

        // Applying the plan is a consequence of having resolved one, not of this render, so it runs
        // past the effect flush instead of cascading a second render straight out of this one. The two
        // calls stay in the same microtask so the pickers and the coverage still land in one commit:
        // syncPickerStateFromCoverage mirrors the applied options into the controlled radio/display
        // state (updateCoverageSettings only updates the form value + pricing), exactly as the
        // QuickEnroll → Customize transition does. The ref above still gates this to once per pet, and
        // it is set synchronously, so a re-run before the microtask cannot apply the plan twice.
        const planToApply = resolvedPlan;
        queueMicrotask(() => {
            syncPickerStateFromCoverage(planToApply);
            updateCoverageSettings(planToApply, false, petId);
        });
    }, [
        autoApplyDefaultPlan,
        activePet?.id,
        activePet?.coverageSettings,
        activePetAllPlans,
        coverageValuesToMatch,
        defaultPlans,
        allPetsHaveFetchedPlans,
        isPetPlansUpdating,
        isQuoteUpdating,
        plansRatingZipcode,
        quote?.ratingZipcode,
        preservedCoverageByPet,
        updateAppState,
        updateCoverageSettings,
        syncPickerStateFromCoverage
    ]);

    useEffect(() => {
        if (!!policies && policies.length === 1 && !isPetPlansUpdating && !isQuoteUpdating) {
            updateAppState({ isApplyAllHidden: true });
        }
        if (petPlans !== undefined && !!policies && policies?.length > 1 && !isPetPlansUpdating && !isQuoteUpdating && !allPetPlansHaveSameOptions) {
            updateAppState({ isApplyAllHidden: true, isMultiPetLinked: false });
        }
    }, [allPetPlansHaveSameOptions, isPetPlansUpdating, isQuoteUpdating, petPlans, policies, updateAppState]);

    const shouldShowLoader = !coveragePresets.length || !activePet || !underwriter || isPetPlansUpdating;

    useEffect(() => {
        updateAppState({ isFormLoaderVisible: shouldShowLoader });
    }, [shouldShowLoader, updateAppState]);

    const coverageSettings = {
        annualLimitValue,
        reimbursementValue,
        annualDeductibleValue,
        activePet,
        allPets,
        floatingVariant,
        termsInModal,
        isMultiPetLinked,
        activePetCoverage,
        defaultPlanConfig
    };

    const handlers = {
        onAnnualLimitChange: (value: any) => {
            registerEdit(); // D1: counts toward the nudge threshold
            setAnnualLimitValue(value);
            handleCustomAmounts({ change: { setting: "annualLimit", value } });
        },
        onReimbursementChange: (value: any) => {
            registerEdit(); // D1: counts toward the nudge threshold
            setReimbursementValue(value);
            handleCustomAmounts({ change: { setting: "reimbursementRate", value } });
        },
        onDeductibleChange: (value: any) => {
            registerEdit(); // D1: counts toward the nudge threshold
            setAnnualDeductibleValue(value);
            handleCustomAmounts({ change: { setting: "annualDeductible", value } });
        }
    };

    const renderCustomizedPlans = () => {
        const petData = {
            activePet,
            policies,
            allPetsHaveSameCoverages,
            quoteId: quote?.id
        };

        const coverageDisplayConfig = {
            samplePolicyUrl,
            showTelehealth,
            isDentalCovered: isDentalCovered,
            isMicrochippingCovered,
            preExistingConditions,
            waitingPeriods,
            showCompellingStats,
            showBreedSpecificConditions: showBreedSpecificConditions && shouldShowBreedSpecificBadges,
            hasAnyBreedConditions: hasAnyBreedConditions && shouldShowBreedSpecificBadges,
            breedConditionData: breedConditionData,
            // show not covered test
            showNotCovered: showNotCovered,
            onSwitchToAccidentAndIllness: () => {
                setCoverageLevel("accidentAndIllness");
                handleCoverageLevelChange("accidentAndIllness");
            }
            // test end
        };

        // actionable section title test
        const handleOpenModal = () => {
            const modalProps = {
                ...petData,
                ...coverageDisplayConfig,
                updateCoverageSettings,
                showStepLabel
            };
            showModal("coverage-details-modal", <FullCoverageDetailsContent {...modalProps} />, Strings.COVERAGE_DETAILS);
        };

        const petAndCoverages = (
            <>
                {!!activePet && (
                    <>
                        <CustomizationSlot
                            type="below-stepper"
                            data={quote}
                            formId={formId}
                            formStepId={UsQuoteFormSteps.COVERAGE.id}
                            formData={props.value?.extra?.formData}
                            updateData={data => builderUtils.updateQuoteExtraData({ quote: props.value, newDataArray: data, updateQuote: () => updateQuote })}
                            priorityCode={priorityCode}
                        />
                        <div className="flex flex-row justify-between empty:hidden">
                            <CustomizationSlot
                                type="above-card"
                                data={quote}
                                formId={formId}
                                formStepId={UsQuoteFormSteps.COVERAGE.id}
                                formData={props.value?.extra?.formData}
                                updateData={data => builderUtils.updateQuoteExtraData({ quote: props.value, newDataArray: data, updateQuote: () => updateQuote })}
                                priorityCode={priorityCode}
                                initialContent={initialAboveCardContent}
                                activePetIndex={appState.activePetIndex}
                            />
                            {hasAboveCardDecor && (
                                <div className="relative py-2 sm:py-0 sm:pb-2 lg:mr-3">
                                    <Button
                                        data-dd-action-name={Strings.DATA_DOG.COVERAGE_STEP_CTA}
                                        onClick={async () => {
                                            await onFormContinue?.();
                                        }}
                                        size="sm"
                                        type="button"
                                        endDecorator={!canContinue || isValidating ? <LoadingSpinner size="xs" /> : <FontAwesomeIcon icon={faChevronRight} />}
                                        disabled={!canContinue || isValidating}
                                        className="rounded-full focus:rounded-full"
                                        ref={continueButtonRef}
                                    >
                                        <div className="text-sm">{Strings.QUICK_CHECKOUT}</div>
                                    </Button>
                                </div>
                            )}
                        </div>
                        {userModalConfig && <UserModal isOpen={isUserModalOpen} onClose={() => setIsUserModalOpen(false)} config={userModalConfig} />}

                        {/* xs only: keep the edit-user trigger above the pet selector; sm+ renders it in the card's Add a Pet row (MultiPetSelect `userEditTrigger`).
                            Gravitas arm: the mockup puts 12px between the trigger and the pet card at 2xs/xs. The column above
                            supplies 16px of flex `gap-4`, so that 12px is reached by pulling 4px back rather than by adding a
                            margin the gap would sit on top of. */}
                        {userEditTrigger && <div className={cn("flex justify-center sm:hidden", showOwnerCtaGravitas ? "-mb-1" : "xs:mb-0")}>{userEditTrigger}</div>}

                        <MultiPetSelect
                            variant={multiPetVariant}
                            policies={policies ?? []}
                            activePet={activePet}
                            setActivePetIndex={value => updateAppState({ activePetIndex: value })}
                            policyPets={policyPets}
                            handleActivePetChange={handleActivePetChange}
                            isMultiPetLinked={isMultiPetLinked}
                            isApplyAllHidden={isApplyAllHidden}
                            isQuoteUpdating={isQuoteUpdating}
                            priceInfoData={priceInfoData}
                            updateCoverageSettings={updateCoverageSettings}
                            policiesCount={quote?.policies?.length ?? 0}
                            petModalConfig={petModalConfig}
                            allPetsHaveSameCoverages={allPetsHaveSameCoverages}
                            showStepLabel={showStepLabel}
                            userEditTrigger={userEditTrigger}
                            multipetToggle={
                                <MultipetToggle
                                    quoteId={quote?.id}
                                    policies={policies}
                                    allPetsHaveSameCoverages={allPetsHaveSameCoverages}
                                    activePet={activePet}
                                    updateCoverageSettings={updateCoverageSettings}
                                    fieldId="all-pets-below-cards"
                                />
                            }
                            showStickyPriceLabel={showStickyPriceLabel}
                        />

                        <>
                            {/* ENG-17301: ref (not an added sentinel node) so the coverage card's own box is the
                                anchor for the checkout-CTA scroll window and the control group's layout is untouched. */}
                            <div ref={coverageEndRef} className="relative">
                                <div
                                    className={cn(
                                        "mt-2 flex flex-col gap-y-4 rounded-3xl border border-stroke-primary bg-background-primary py-3 shadow-md lg:grid lg:grid-cols-3 lg:p-0",
                                        showNumbered && ["border-t-0", "xs:border-t", "sm:border-t-0", "md:border-t-0", "lg:border-t"]
                                    )}
                                >
                                    {/* --- LEFT COLUMN (Desktop Only) --- */}
                                    <div className="hidden p-4 max-lg:border-b sm:px-6 md:pt-6 lg:flex lg:flex-col lg:gap-4 lg:border-r lg:border-stroke-primary lg:px-4 lg:pt-4 xl:gap-4 xl:p-6">
                                        <div>
                                            <div className={cn("flex items-center justify-between", showNumbered && "mb-2")}>
                                                <div className={cn("mb-2 text-sm font-semibold", showNumbered && "flex items-center gap-1 text-lg font-bold leading-7")}>
                                                    {showNumbered && (
                                                        <div className="size-8">
                                                            <OneIcon data-testid="one-icon-numbered" />
                                                        </div>
                                                    )}
                                                    Coverage
                                                </div>
                                                {showStepLabel && (
                                                    <MultipetToggle
                                                        quoteId={quote?.id}
                                                        policies={policies}
                                                        allPetsHaveSameCoverages={allPetsHaveSameCoverages}
                                                        activePet={activePet}
                                                        updateCoverageSettings={updateCoverageSettings}
                                                        fieldId="all-pets-coverage-label"
                                                    />
                                                )}
                                            </div>
                                            <CoverageTypeControl
                                                coverageTypes={coverageTypes}
                                                coverageLevel={coverageLevel}
                                                onCoverageLevelChange={newLevel => {
                                                    setCoverageLevel(newLevel);
                                                    handleCoverageLevelChange(newLevel);
                                                }}
                                                floatingVariant={floatingVariant}
                                            />
                                        </div>

                                        <CoverageParameterControls
                                            settings={coverageSettings}
                                            handlers={handlers}
                                            showReimbursementTip={showReimbursementTip}
                                            isTermsDefinitionsEnabled={isTermsDefinitionsEnabled}
                                            view="desktop"
                                        />
                                        <ReimbursementTerms termsInModal={termsInModal} wrapperClass="justify-center" isTermsDefinitionsEnabled={isTermsDefinitionsEnabled} />

                                        {shouldShowBreedSpecificBadges && breedConditionData && <BreedConditionFacts breedConditionData={breedConditionData} />}
                                    </div>

                                    {/* --- RIGHT COLUMN (Desktop) / MAIN STACK (Mobile) --- */}
                                    <div className="flex flex-col gap-4 lg:col-span-2 lg:p-4 xl:p-6 xl:pb-4">
                                        {/* On mobile, show Section Header here */}
                                        <div className="flex flex-col gap-1 px-4 sm:px-6 lg:hidden lg:px-0">
                                            <CoverageSectionHeader
                                                petData={petData}
                                                displayConfig={coverageDisplayConfig}
                                                updateCoverageSettings={updateCoverageSettings}
                                                showStepLabel={showStepLabel}
                                                showNumbered={showNumbered}
                                                showFullCoverageDetails={showFullCoverageDetails}
                                                showDetailsCtaGravitas={showDetailsCtaGravitas}
                                            />

                                            <CoverageTypeControl
                                                coverageTypes={coverageTypes}
                                                coverageLevel={coverageLevel}
                                                onCoverageLevelChange={newLevel => {
                                                    setCoverageLevel(newLevel);
                                                    handleCoverageLevelChange(newLevel);
                                                }}
                                                floatingVariant={floatingVariant}
                                            />

                                            {/* // Actionable section title test — 2xs/xs only; sm+ keeps the in-header button */}
                                            {showFullCoverageDetails && (
                                                <div className="mt-4 sm:hidden" data-testid="actionable-section-title">
                                                    <FullCoverageDetailsButton
                                                        onClick={handleOpenModal}
                                                        showFullCoverageDetails={showFullCoverageDetails}
                                                        buttonClass="w-full justify-center"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {isAiInsightsEnabled && showAiInsights && (
                                            <div className="hidden lg:block">
                                                <AiInsights pets={petsForInsights} />
                                            </div>
                                        )}

                                        {/* The display is always in this column */}
                                        <CoverageIconGrid
                                            coverageInfo={activePetCoverage?.config}
                                            species={activePet?.species}
                                            underwriter={underwriter as PetUnderwriterType}
                                            displayConfig={coverageDisplayConfig}
                                            showDetailsCtaGravitas={showDetailsCtaGravitas}
                                        />

                                        {/* On mobile, show the parameter controls here */}
                                        <div className="flex flex-col gap-3 border-t border-stroke-primary px-4 pt-4 sm:px-6 lg:hidden lg:px-0">
                                            {isAiInsightsEnabled && showAiInsights && <AiInsights pets={petsForInsights} />}
                                            <CoverageParameterControls
                                                settings={coverageSettings}
                                                handlers={handlers}
                                                showReimbursementTip={showReimbursementTip}
                                                isTermsDefinitionsEnabled={isTermsDefinitionsEnabled}
                                                view="mobile"
                                            />
                                            <ReimbursementTerms termsInModal={termsInModal} wrapperClass="justify-center" isTermsDefinitionsEnabled={isTermsDefinitionsEnabled} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* AI Insights Disclaimer - shows when AI is enabled */}
                            {isAiInsightsEnabled && showAiInsights && hasAnyPetWithAiInsights && (
                                <div className="flex flex-col gap-2 text-xs text-content-secondary">
                                    <div>
                                        <sup>
                                            <FontAwesomeIcon icon={faSparkles} />
                                        </sup>
                                        Spot AI pulls claim reimbursement data based on actual policyholder claims 2024-2025. Reimbursement amounts may vary depending on the
                                        specific terms of the policy, including coverage selected, annual deductible, reimbursement rate, and annual limit. Individual experiences
                                        and outcomes may vary.
                                    </div>
                                </div>
                            )}

                            {/* Breed-Specific Disclaimers - shows when breed badges are enabled */}
                            {hasAnyBreedConditions && shouldShowBreedSpecificBadges && (
                                <div className="flex flex-col gap-2 text-center text-xs text-content-secondary">
                                    <div>
                                        <sup>1</sup>
                                        2025 Spot Pet Insurance Services, LLC. claims data. Reimbursement amounts are based on actual claims and may vary depending on the policy
                                        terms, including selected coverage and limits. Individual outcomes may differ.
                                    </div>
                                </div>
                            )}
                        </>
                    </>
                )}
            </>
        );

        return (
            <CoverageDisplayProvider
                coverageInfo={activePetCoverage?.config}
                species={activePet?.species}
                samplePolicyUrl={samplePolicyUrl}
                isDentalCovered={isDentalCovered}
                isMicrochippingCovered={isMicrochippingCovered}
                showBreedSpecificConditions={shouldShowBreedSpecificBadges && showBreedSpecificConditions}
            >
                {!!activePet && (
                    <div key={activePet.id} className={cn("flex flex-col gap-4", { "mt-3": showRebrandTheme })}>
                        {!showStepLabel && (
                            <div className="flex items-center justify-between">
                                <p className={cn("text-lg font-bold")}>{title}</p>
                                {!isApplyAllHidden && (
                                    <MultipetToggle
                                        quoteId={quote?.id}
                                        policies={policies}
                                        allPetsHaveSameCoverages={allPetsHaveSameCoverages}
                                        activePet={activePet}
                                        updateCoverageSettings={updateCoverageSettings}
                                        wrapperClass={`${showStepLabel ? "shrink-0" : ""}`}
                                        fieldId="all-pets-coverage-header"
                                    />
                                )}
                            </div>
                        )}
                        {/* /// pre-existing conditions test */}
                        {isPreExistingBannerEnabled && preExistingConfig?.top === "pre-existing" && <PreExistingConditionsBanner />}
                        {/* /// Test ends  */}
                        {/* // agg test */}
                        {hasAboveCardDecor ? (
                            <div className="relative -mx-1 rounded-t-2xl px-3 pt-4">
                                <div
                                    className={`before:pointer-events-none before:absolute before:inset-0 before:rounded-t-2xl before:border before:border-stroke-brand-b before:bg-background-brand-b-secondary before:content-[""] before:[-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_50%,transparent_100%)] before:[mask-image:linear-gradient(to_bottom,black_0%,black_50%,transparent_100%)]`}
                                >
                                    {petAndCoverages}
                                </div>
                            </div>
                        ) : (
                            // prod
                            petAndCoverages
                        )}
                        {showTakeoverProvision && <TakeoverProvision />}
                        <div className={cn("mt-6 empty:hidden")}>
                            <CustomizationSlot
                                type="above-preventive"
                                data={quote}
                                formId={formId}
                                formStepId={UsQuoteFormSteps.COVERAGE.id}
                                formData={props.value?.extra?.formData}
                                updateData={data => builderUtils.updateQuoteExtraData({ quote: props.value, newDataArray: data, updateQuote: () => updateQuote })}
                                priorityCode={priorityCode}
                            />
                        </div>
                        {isPreExistingBannerEnabled && preExistingConfig?.middle === "pre-existing" && (
                            <div className="mx-auto mt-2">
                                <PreExistingConditionsBanner />
                            </div>
                        )}
                        {/* ENG-17301: measures where preventive care ends — one of the two closing edges for the
                            checkout CTA. `empty:hidden` so this wrapper adds no flex gap on the forms/pets where
                            PreventiveCare renders nothing. */}
                        <div ref={preventiveEndRef} data-testid="preventive-care-section" className="empty:hidden">
                            <PreventiveCare
                                activePet={activePet as Pet}
                                preventiveOptions={preventiveCareOptions}
                                preventiveConfig={preventiveConfig}
                                underwriter={underwriter as PetUnderwriterType}
                                wellnessValue={wellnessValue}
                                handleChange={(value, petId) => {
                                    setWellnessValue(value as any);
                                    handlePreventiveChange(value, petId);
                                }}
                                showNumbered={showNumbered}
                                showPlatinumRecommendation={showPlatinumRecommendation}
                                showClickableCards={showClickableCards}
                                prevCareCompare={prevCareCompareVariant}
                            />
                        </div>

                        {!!exclusions && (
                            <div className="flex flex-col">
                                {React.cloneElement(exclusions as React.ReactElement<Record<string, unknown>>, {
                                    showBreedSpecificConditions: showBreedSpecificConditions,
                                    hasAnyBreedConditions: hasAnyBreedConditions,
                                    activePet: activePet,
                                    policies: policies
                                })}
                            </div>
                        )}
                        {!!customizationSlot && <>{customizationSlot}</>}

                        {/* Modal needed for: alternateSubmitButton */}
                        <PetModal
                            initialValues={{ policies: [getModalInitialValues(activePet?.coverageSettings, !!isMultiPetLinked)] }}
                            isOpen={addPetModal === ADD_PET_MODAL_TRIGGERS.ALT_SUBMIT_CTA}
                            onClose={() => updateAppState({ addPetModal: undefined })}
                            config={petModalConfig}
                            handleActivePetChange={handleActivePetChange}
                        />
                    </div>
                )}
            </CoverageDisplayProvider>
        );
    };

    return (
        <CoverageEditorProvider editorConfig={props.editorConfig} preventiveCareOptions={preventiveCareOptions}>
            <Heading level="h1" className="sr-only">
                Coverage
            </Heading>
            {!shouldShowLoader && (showQuickEnrollUI ? <QuickEnrollPlans pets={existingPolicies ?? []} newPetName={policies?.[0]?.name} /> : <>{renderCustomizedPlans()}</>)}
            {/* Not gated on shouldShowLoader: a pet-plans refetch (e.g. "Add a pet") would
                otherwise unmount the guide and drop a toast/selection state mid-interaction. */}
            {isMultiPlanGuideEnabled && !showQuickEnrollUI && (
                <MultiPlanGuide isNudgeVisible={isNudgeVisible} dismissNudge={dismissNudge} groups={recommendedPlanGroups} onApplyPlan={applyRecommendedPlan} />
            )}
            {/* ENG-17301 variant 2 — variant 1 renders its CTA inside the sticky menu instead.
                Yields to the multi-plan toast (z-45, same step, also US-only) rather than stacking
                under it, the same way the gift-card toast does via `hideForMultiPlanToast`. */}
            {checkoutCtaVariant === CHECKOUT_CTA_VARIANTS.FLOATING && !showQuickEnrollUI && (
                <FloatingCheckoutCta isVisible={isCheckoutCtaVisible && !shouldShowLoader && !appState.multiPlanToastVisible} formId={formId} />
            )}
            {modal.render}
        </CoverageEditorProvider>
    );
}

type CoverageEditorContextValue = CustomCoverageProps["editorConfig"] & {
    preventiveCareOptions?: PreventiveOption[];
};

const CoverageEditorContext = createContext<CoverageEditorContextValue | undefined>(undefined);

export const useCoverageEditorContext = () => {
    const context = useContext(CoverageEditorContext);
    if (context === undefined) {
        throw new Error("useCoverageEditorContext must be used within a CoverageEditorProvider");
    }
    return context;
};

interface CoverageEditorProviderProps {
    children: ReactNode;
    editorConfig: CustomCoverageProps["editorConfig"];
    preventiveCareOptions?: PreventiveOption[];
}

export const CoverageEditorProvider: React.FC<CoverageEditorProviderProps> = ({ children, editorConfig, preventiveCareOptions }) => {
    const contextValue = useMemo(
        () => ({
            ...editorConfig,
            preventiveCareOptions
        }),
        [editorConfig, preventiveCareOptions]
    );

    return <CoverageEditorContext.Provider value={contextValue}>{children}</CoverageEditorContext.Provider>;
};

export const useCoverageEditorContextSafe = () => {
    return useContext(CoverageEditorContext);
};
